//Oct10 assetPromotionConfig.js (diagnostics w/ console.log):
// src/utils/assetPromotionConfig.js
import { db } from '../firebase';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { safeGetItem, safeSetItem, safeRemoveItem } from './storageUtils';

/* ─────────────────────────────────────────────────────────────
   Cache: in-memory + storageUtils (session+local with timestamp)
   - In-memory = fastest per-tab
   - storageUtils = consistent {value,timestamp} handling, LRU cleanup
   - Small TTL for mem cache, storageUtils handles aging
   ──────────────────────────────────────────────────────────── */
const MEM_CACHE = new Map(); // orgId -> { cfg, ts }
const TTL_MS = 60_000;       // 60s in-memory TTL (tiny, config is small)

const now = () => Date.now();
const keyFor = (orgId) => `org_${orgId}_assetPromotionConfig`;

const normalizeCfg = (raw) => {
  const cfg = raw || {};
  return {
    keys: Array.isArray(cfg.keys) ? cfg.keys : [],
    arrayKeys: Array.isArray(cfg.arrayKeys) ? cfg.arrayKeys : [],
    dateKeys: Array.isArray(cfg.dateKeys) ? cfg.dateKeys : [],
  };
};

const getOrgRef = (orgId) => doc(db, 'organizations', orgId);

const memRead = (orgId) => {
  const hit = MEM_CACHE.get(orgId);
  if (!hit) {
    console.log('[assetPromotionConfig] memRead: MISS (no entry)', { orgId });
    return null;
  }
  const age = now() - hit.ts;
  if (age > TTL_MS) {
    console.log('[assetPromotionConfig] memRead: STALE → evict', { orgId, age, TTL_MS });
    MEM_CACHE.delete(orgId);
    return null;
  }
  console.log('[assetPromotionConfig] memRead: HIT', {
    orgId,
    age,
    TTL_MS,
    cfgPreview: {
      keys: hit.cfg?.keys?.length ?? 0,
      arrayKeys: hit.cfg?.arrayKeys?.length ?? 0,
      dateKeys: hit.cfg?.dateKeys?.length ?? 0,
    },
  });
  return hit.cfg;
};

const memWrite = (orgId, cfg) => {
  MEM_CACHE.set(orgId, { cfg, ts: now() });
  console.log('[assetPromotionConfig] memWrite', {
    orgId,
    cfgPreview: {
      keys: cfg?.keys?.length ?? 0,
      arrayKeys: cfg?.arrayKeys?.length ?? 0,
      dateKeys: cfg?.dateKeys?.length ?? 0,
    },
  });
};

/* ─────────────────────────────────────────────────────────────
   Public API
   ──────────────────────────────────────────────────────────── */

/**
 * Load the org's asset promotion config:
 *   organizations/{orgId}.assetPromotionConfig = { keys:[], arrayKeys:[], dateKeys:[] }
 */
export async function loadAssetPromotionConfig(organizationId, { bypassCache = false } = {}) {
  if (!organizationId) throw new Error('organizationId is required');

  console.log('[assetPromotionConfig] load: start', {
    organizationId,
    bypassCache,
  });

  // 1) in-memory
  if (!bypassCache) {
    const mem = memRead(organizationId);
    if (mem) {
      console.log('[assetPromotionConfig] load: RETURN from MEM', {
        organizationId,
        cfgPreview: {
          keys: mem.keys.length,
          arrayKeys: mem.arrayKeys.length,
          dateKeys: mem.dateKeys.length,
        },
      });
      return mem;
    }

    // 2) storageUtils (session/local with {value,timestamp})
    const stored = safeGetItem(keyFor(organizationId));
    if (stored) {
      const isWrapped = stored && typeof stored === 'object' && Object.prototype.hasOwnProperty.call(stored, 'value');
      console.log('[assetPromotionConfig] load: STORAGE HIT', {
        organizationId,
        storageKey: keyFor(organizationId),
        isWrapped,
      });

      // Unwrap { value, timestamp } shape used by storageUtils
      const raw = isWrapped ? stored.value : stored;
      const cfg = normalizeCfg(raw);
      console.log('[assetPromotionConfig] load: STORAGE → NORMALIZED', {
        organizationId,
        cfgPreview: {
          keys: cfg.keys.length,
          arrayKeys: cfg.arrayKeys.length,
          dateKeys: cfg.dateKeys.length,
        },
      });
      memWrite(organizationId, cfg);
      console.log('[assetPromotionConfig] load: RETURN from STORAGE (post memWrite)', {
        organizationId,
      });
      return cfg;
    } else {
      console.log('[assetPromotionConfig] load: STORAGE MISS', {
        organizationId,
        storageKey: keyFor(organizationId),
      });
    }
  } else {
    console.log('[assetPromotionConfig] load: bypassCache=true (skip MEM & STORAGE)', { organizationId });
  }

  // 3) Firestore
  console.log('[assetPromotionConfig] load: fetching from Firestore', { organizationId });
  const snap = await getDoc(getOrgRef(organizationId));
  const raw = snap.exists() ? snap.data()?.assetPromotionConfig : null;
  const cfg = normalizeCfg(raw);

  console.log('[assetPromotionConfig] load: FIRESTORE RESULT', {
    organizationId,
    exists: snap.exists(),
    rawPreview: raw ? {
      keys: Array.isArray(raw.keys) ? raw.keys.length : 'n/a',
      arrayKeys: Array.isArray(raw.arrayKeys) ? raw.arrayKeys.length : 'n/a',
      dateKeys: Array.isArray(raw.dateKeys) ? raw.dateKeys.length : 'n/a',
    } : null,
    normalized: {
      keys: cfg.keys.length,
      arrayKeys: cfg.arrayKeys.length,
      dateKeys: cfg.dateKeys.length,
    },
  });

  memWrite(organizationId, cfg);
  safeSetItem(keyFor(organizationId), cfg); // storageUtils will wrap; we unwrap on read

  console.log('[assetPromotionConfig] load: RETURN from FIRESTORE (post memWrite + safeSetItem)', {
    organizationId,
  });

  return cfg;
}

/**
 * Which bucket is this UFI in?
 * Returns: 'key' | 'array' | 'date' | null
 */
export function bucketOf(cfg, ufi) {
  const bucket =
    !cfg || !ufi ? null :
    (cfg.keys.includes(ufi) ? 'key' :
    (cfg.arrayKeys.includes(ufi) ? 'array' :
    (cfg.dateKeys.includes(ufi) ? 'date' : null)));

  console.log('[assetPromotionConfig] bucketOf', {
    ufi,
    cfgPreview: cfg ? {
      keys: cfg.keys.length,
      arrayKeys: cfg.arrayKeys.length,
      dateKeys: cfg.dateKeys.length,
    } : null,
    bucket,
  });

  return bucket;
}

/**
 * Promote a UFI into exactly one bucket (and remove from the others).
 * kind: 'key' | 'array' | 'date'
 */
export async function promoteUfi(organizationId, ufi, kind) {
  if (!organizationId) throw new Error('organizationId is required');
  if (!ufi) throw new Error('ufi is required');
  if (!['key', 'array', 'date'].includes(kind)) throw new Error('invalid kind');

  console.log('[assetPromotionConfig] promoteUfi: start', {
    organizationId, ufi, kind,
  });

  const ref = getOrgRef(organizationId);

  // Ensure the container doc / field exists to avoid updateDoc errors
  console.log('[assetPromotionConfig] promoteUfi: setDoc ensure container', { organizationId });
 // Do NOT pre-create assetPromotionConfig; updateDoc with dotted paths
 // will create nested maps/arrays as needed without overwriting others.

  const pathFor = (k) =>
    k === 'key'
      ? 'assetPromotionConfig.keys'
      : k === 'array'
      ? 'assetPromotionConfig.arrayKeys'
      : 'assetPromotionConfig.dateKeys';

  const target = pathFor(kind);
  const others = ['key', 'array', 'date'].filter((k) => k !== kind).map(pathFor);

  // Atomic Firestore update: add to target, remove from others
  const payload = { [target]: arrayUnion(ufi) };
  others.forEach((p) => (payload[p] = arrayRemove(ufi)));

  console.log('[assetPromotionConfig] promoteUfi: updateDoc payload', {
    organizationId, ufi, kind, target, others,
  });

  await updateDoc(ref, payload);

  // Eagerly update caches to avoid a re-fetch
  console.log('[assetPromotionConfig] promoteUfi: eager cache refresh (bypassCache=true)', {
    organizationId,
  });
  const prev = await loadAssetPromotionConfig(organizationId, { bypassCache: true }).catch((err) => {
    console.warn('[assetPromotionConfig] promoteUfi: load(prev) FAILED', {
      organizationId,
      error: err?.message || String(err),
    });
    return null;
  });

  const base = prev
    ? {
        keys: prev.keys.filter((x) => x !== ufi),
        arrayKeys: prev.arrayKeys.filter((x) => x !== ufi),
        dateKeys: prev.dateKeys.filter((x) => x !== ufi),
      }
    : { keys: [], arrayKeys: [], dateKeys: [] };

  if (!prev) {
    console.warn('[assetPromotionConfig] promoteUfi: prev config is NULL → rebuilding base from EMPTY (this can clobber other UFIs if Firestore read failed).', {
      organizationId, ufi, kind,
    });
  } else {
    console.log('[assetPromotionConfig] promoteUfi: prev exists; removing current ufi then pushing into target bucket', {
      organizationId, ufi, kind,
      prevPreview: {
        keys: prev.keys.length,
        arrayKeys: prev.arrayKeys.length,
        dateKeys: prev.dateKeys.length,
      },
    });
  }

  if (kind === 'key') base.keys.push(ufi);
  if (kind === 'array') base.arrayKeys.push(ufi);
  if (kind === 'date') base.dateKeys.push(ufi);

  console.log('[assetPromotionConfig] promoteUfi: final base to cache', {
    organizationId, ufi, kind,
    basePreview: {
      keys: base.keys.length,
      arrayKeys: base.arrayKeys.length,
      dateKeys: base.dateKeys.length,
    },
  });

  memWrite(organizationId, base);
  safeSetItem(keyFor(organizationId), base);

  console.log('[assetPromotionConfig] promoteUfi: done (mem + storage updated)', {
    organizationId,
    storageKey: keyFor(organizationId),
  });

  return base;
}

/**
 * Demote a UFI from all buckets.
 */
export async function demoteUfi(organizationId, ufi) {
  if (!organizationId) throw new Error('organizationId is required');
  if (!ufi) throw new Error('ufi is required');

  console.log('[assetPromotionConfig] demoteUfi: start', {
    organizationId, ufi,
  });

  const ref = getOrgRef(organizationId);
  const payload = {
    'assetPromotionConfig.keys': arrayRemove(ufi),
    'assetPromotionConfig.arrayKeys': arrayRemove(ufi),
    'assetPromotionConfig.dateKeys': arrayRemove(ufi),
  };

  console.log('[assetPromotionConfig] demoteUfi: updateDoc payload', {
    organizationId, ufi,
  });

  await updateDoc(ref, payload);

  console.log('[assetPromotionConfig] demoteUfi: eager cache refresh (bypassCache=true)', {
    organizationId,
  });
  const prev = await loadAssetPromotionConfig(organizationId, { bypassCache: true }).catch((err) => {
    console.warn('[assetPromotionConfig] demoteUfi: load(prev) FAILED', {
      organizationId,
      error: err?.message || String(err),
    });
    return null;
  });

  const next = prev
    ? {
        keys: prev.keys.filter((x) => x !== ufi),
        arrayKeys: prev.arrayKeys.filter((x) => x !== ufi),
        dateKeys: prev.dateKeys.filter((x) => x !== ufi),
      }
    : { keys: [], arrayKeys: [], dateKeys: [] };

  if (!prev) {
    console.warn('[assetPromotionConfig] demoteUfi: prev config is NULL → rebuilding next from EMPTY (this can clobber other UFIs if Firestore read failed).', {
      organizationId, ufi,
    });
  } else {
    console.log('[assetPromotionConfig] demoteUfi: prev exists; removing current ufi from all buckets', {
      organizationId, ufi,
      prevPreview: {
        keys: prev.keys.length,
        arrayKeys: prev.arrayKeys.length,
        dateKeys: prev.dateKeys.length,
      },
    });
  }

  memWrite(organizationId, next);
  safeSetItem(keyFor(organizationId), next);

  console.log('[assetPromotionConfig] demoteUfi: done (mem + storage updated)', {
    organizationId,
    storageKey: keyFor(organizationId),
    nextPreview: {
      keys: next.keys.length,
      arrayKeys: next.arrayKeys.length,
      dateKeys: next.dateKeys.length,
    },
  });

  return next;
}

/**
 * Overwrite the full config (admin tools / migrations).
 */
export async function setAssetPromotionConfig(
  organizationId,
  { keys = [], arrayKeys = [], dateKeys = [] } = {}
) {
  if (!organizationId) throw new Error('organizationId is required');

  console.log('[assetPromotionConfig] setAssetPromotionConfig: start', {
    organizationId, keysCount: keys.length, arrayKeysCount: arrayKeys.length, dateKeysCount: dateKeys.length,
  });

  const cfg = normalizeCfg({ keys, arrayKeys, dateKeys });

  console.log('[assetPromotionConfig] setAssetPromotionConfig: write to Firestore', {
    organizationId,
    cfgPreview: {
      keys: cfg.keys.length,
      arrayKeys: cfg.arrayKeys.length,
      dateKeys: cfg.dateKeys.length,
    },
  });

  await setDoc(getOrgRef(organizationId), { assetPromotionConfig: cfg }, { merge: true });

  memWrite(organizationId, cfg);
  safeSetItem(keyFor(organizationId), cfg);

  console.log('[assetPromotionConfig] setAssetPromotionConfig: done (mem + storage updated)', {
    organizationId,
    storageKey: keyFor(organizationId),
  });

  return cfg;
}

/**
 * Invalidate caches (useful after admin ops or tests).
 * If orgId omitted, clears only in-memory for all; leaves storage entries intact.
 */
export function invalidateAssetPromotionCache(organizationId) {
  if (organizationId) {
    console.log('[assetPromotionConfig] invalidate: single org', {
      organizationId,
      storageKey: keyFor(organizationId),
    });
    MEM_CACHE.delete(organizationId);
    safeRemoveItem(keyFor(organizationId));
  } else {
    console.log('[assetPromotionConfig] invalidate: ALL orgs (mem only)');
    MEM_CACHE.clear();
  }
}
